// [목적] 축제 댓글 도메인 zod 스키마 — /festivals/:id/comments BE/FE 공유
// [이유] festival-comments Spec §5-4 — 폴리오 댓글(folio-comment.ts)과 UI·로직을 공유하되 대상 키가 다르다
//         (portfolioId uuid ↔ festivalContentId text: 축제는 DB 엔티티가 아니라 TourAPI contentId).
// [사이드 이팩트] apps/api(festival-comment 모듈) + apps/web(FestivalDetailPage 댓글 섹션) 양쪽 회귀 검증 필요.
//               packages/types 는 dist 를 참조하므로 수정 후 빌드해야 소비 측에서 해석된다.
// [수정 시 고려사항] 작성자 스키마는 folio-comment.ts 의 commentAuthorSchema 를 그대로 공유한다.
//                   입력/목록 쿼리 스키마는 폴리오와 필드가 같지만, 도메인별 한도 조정 여지를 위해 별도 export 로 둔다.

import { z } from 'zod';

import { commentAuthorSchema } from './folio-comment.js';

/** festival_comments 테이블 직렬화 형태. soft-delete 시 body=null + isDeleted=true (tombstone). */
export const festivalCommentSchema = z.object({
  id: z.string().uuid(),
  festivalContentId: z.string(),
  authorUserId: z.string().uuid().nullable(),
  parentId: z.string().uuid().nullable(),
  body: z.string().nullable(), // soft-delete 시 null
  hidden: z.boolean(),
  isDeleted: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
  /** 작성자 메타. users LEFT JOIN — 탈퇴/익명 시 null */
  author: commentAuthorSchema.nullable().optional(),
});

/** POST /festivals/:id/comments 요청 body. parentId 는 대댓글(1-level) 시에만 지정. */
export const createFestivalCommentInputSchema = z.object({
  text: z.string().min(1).max(1000),
  parentId: z.string().uuid().optional(),
});

/** PATCH /festivals/:id/comments/:commentId 요청 body. */
export const patchFestivalCommentInputSchema = z.object({
  text: z.string().min(1).max(1000),
});

/** GET /festivals/:id/comments 쿼리 파라미터. */
export const festivalCommentListQuerySchema = z.object({
  cursor: z.string().max(500).optional(),
  parentId: z.string().uuid().optional(),
  limit: z.coerce.number().int().min(1).max(50).default(20),
});

export type FestivalComment = z.infer<typeof festivalCommentSchema>;
export type CreateFestivalCommentInput = z.infer<typeof createFestivalCommentInputSchema>;
export type PatchFestivalCommentInput = z.infer<typeof patchFestivalCommentInputSchema>;
export type FestivalCommentListQuery = z.infer<typeof festivalCommentListQuerySchema>;
